/**
 * Transports & mobilité — arrêts de transport en commun autour de la fiche.
 *
 * Source : OpenStreetMap via Overpass, mêmes mirrors que `commerces.ts`.
 * OSM est la meilleure base publique pour les arrêts tous modes confondus :
 * SNCF/Open Data publie les gares mais pas les arrêts urbains ; Chouette /
 * transport.data.gouv agrège les GTFS AO par AO avec des schémas hétérogènes.
 *
 * Catégories retenues :
 *   - metro   — `station=subway` OR `railway=station + station=subway`
 *   - tram    — `railway=tram_stop` OR `station=light_rail`
 *   - train   — `railway=station` (hors métro/tram)
 *   - bus     — `highway=bus_stop` (et `public_transport=platform + bus=yes`)
 *
 * Rayon piloté par le tier de la commune :
 *   rural       3 km   — arrêts bus/gare éloignés en commune < 2 k
 *   petite      2 km
 *   moyenne     1,5 km
 *   grande      1 km
 *   métropole   600 m  — Paris / Lyon / Marseille : un arrêt de bus à < 600 m
 *                        c'est ~6 min à pied, le critère "walkable transit"
 *
 * Les arrêts de bus sont capés à 25 items (les lignes dédoublent chaque poteau
 * comme un node distinct dans OSM — inutile d'en rendre 80 dans la carte, on
 * affiche le compte total et la liste des 8 plus proches).
 */

import { useMemo } from "react";
import { coordKey, haversineMeters, useEnvBlock } from "./shared";
import { useCommuneInfo, type LocalityTier } from "./localityTier";
import type { GeocodeResult } from "../types";
import { fetchTransportsNational } from "./transportsNational";

export const TRANSPORTS_RADIUS_BY_TIER: Record<LocalityTier, number> = {
  rural: 3_000,
  petite: 2_000,
  moyenne: 1_500,
  grande: 1_000,
  metropole: 600,
};

const TRANSPORTS_TTL_DAYS = 30;

// Same dual-mirror setup as commerces. Matches a single Overpass surface
// so a 429 on the primary transparently falls through to the secondary.
// NB: overpass.openstreetmap.fr (OSM-FR) rejects cross-origin calls from
// non-allowlisted domains (403 + missing CORS header) — not usable client-side.
const OVERPASS_ENDPOINTS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
];
const OVERPASS_TIMEOUT_S = 45;

export type TransportMode = "metro" | "tram" | "train" | "bus";

export type TransportItem = {
  id: string;
  name: string;
  mode: TransportMode;
  // Line numbers / reseau (e.g. "7B", "T3a", "RER A"). Joined as displayed.
  lines: string[];
  lat: number;
  lng: number;
  distance_m: number;
};

export type TransportsData = {
  center: { lat: number; lng: number };
  radius_m: number;
  tier: LocalityTier;
  fetchedAt: string;
  counts: Record<TransportMode, number>;
  // Up to 40 items total after dedup + per-mode caps — enough for the card
  // to show top 8 per tab without needing a second fetch.
  items: TransportItem[];
};

// ─── Fetch ──────────────────────────────────────────────────────────────

export async function fetchTransports(
  lat: number,
  lng: number,
  radiusM: number,
  tier: LocalityTier,
): Promise<TransportsData> {
  // Source primaire : base consolidée nationale PAN (transport.data.gouv.fr)
  // pré-indexée en geohash-5 sur Firebase Storage. Si la cellule cible
  // manque (ingestion pas encore exécutée, zone hors couverture), on
  // retombe sur Overpass qui couvre tout OSM mais rate les petits réseaux.
  try {
    return await fetchTransportsNational(lat, lng, radiusM, tier);
  } catch (e) {
    console.warn(
      "[transports] national fallback → Overpass",
      e instanceof Error ? e.message : e,
    );
  }

  return fetchTransportsOverpass(lat, lng, radiusM, tier);
}

async function fetchTransportsOverpass(
  lat: number,
  lng: number,
  radiusM: number,
  tier: LocalityTier,
): Promise<TransportsData> {
  // We intentionally cast a wide net and disambiguate modes client-side
  // in `classifyMode` — Overpass regex gymnastics get brittle when you try
  // to split metro vs tram vs train in the query itself.
  const q = `[out:json][timeout:${OVERPASS_TIMEOUT_S}];
(
  node(around:${radiusM},${lat},${lng})["railway"~"^(station|tram_stop|halt)$"];
  node(around:${radiusM},${lat},${lng})["station"~"^(subway|light_rail)$"];
  node(around:${radiusM},${lat},${lng})["highway"="bus_stop"];
  node(around:${radiusM},${lat},${lng})["public_transport"="station"];
);
out tags;`;

  const body = new URLSearchParams({ data: q });

  let payload: OverpassResponse | null = null;
  let lastErr: unknown = null;
  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
        signal: AbortSignal.timeout((OVERPASS_TIMEOUT_S + 5) * 1000),
      });
      if (!res.ok) {
        lastErr = new Error(`overpass HTTP ${res.status}`);
        continue;
      }
      payload = (await res.json()) as OverpassResponse;
      break;
    } catch (e) {
      lastErr = e;
    }
  }
  if (!payload) {
    throw lastErr instanceof Error ? lastErr : new Error("overpass unreachable");
  }

  // Defensive: if *all* mirrors returned a 200 but an empty `elements` array,
  // it's almost always a transient glitch (the query was rewritten mid-flight,
  // the mirror served a stale empty response, etc.) rather than a genuine
  // "no transit anywhere within 2 km" signal. Throwing here forces the env
  // block infra to record an `error` state (badge "indisponible") which is
  // correctly re-tried on next mount — instead of poisoning the 30-day cache
  // with all-zero counts like we saw on the Le Touquet fiche.
  if (!Array.isArray(payload.elements) || payload.elements.length === 0) {
    throw new Error("overpass returned empty elements — treating as transient");
  }

  const rawItems: TransportItem[] = [];
  for (const el of payload.elements ?? []) {
    if (typeof el.lat !== "number" || typeof el.lon !== "number") continue;
    const tags = el.tags ?? {};
    const mode = classifyMode(tags);
    if (!mode) continue;
    // Many OSM nodes under `public_transport=station` are railway stations
    // with no explicit `railway` tag — already caught by classifyMode.
    // Skip the node when it has no name AND is a bus_stop : a nameless bus
    // stop in the middle of nowhere is usually a mapping draft.
    const name = (tags.name ?? "").trim();
    if (!name && mode === "bus") continue;
    rawItems.push({
      id: `node/${el.id}`,
      name: name || "Arrêt sans nom",
      mode,
      lines: extractLines(tags),
      lat: el.lat,
      lng: el.lon,
      distance_m: Math.round(haversineMeters(lat, lng, el.lat, el.lon)),
    });
  }

  // Dedupe bus stops: OSM maps both directions of a street as separate
  // nodes with the same `name`. Keep the closest.
  const deduped = dedupeBusStops(rawItems);

  deduped.sort((a, b) => a.distance_m - b.distance_m);

  // Per-mode caps : we don't want 30 bus stops to push the train station
  // off the visible list. Caps are generous enough that dense city centres
  // still feel "complete".
  const perModeCap: Record<TransportMode, number> = {
    metro: 12,
    tram: 10,
    train: 8,
    bus: 25,
  };
  const perModeCount: Record<TransportMode, number> = {
    metro: 0, tram: 0, train: 0, bus: 0,
  };
  const items: TransportItem[] = [];
  for (const it of deduped) {
    if (perModeCount[it.mode] >= perModeCap[it.mode]) continue;
    perModeCount[it.mode] += 1;
    items.push(it);
    if (items.length >= 40) break;
  }

  const counts: Record<TransportMode, number> = {
    metro: 0, tram: 0, train: 0, bus: 0,
  };
  for (const it of deduped) counts[it.mode] += 1;

  return {
    center: { lat, lng },
    radius_m: radiusM,
    tier,
    fetchedAt: new Date().toISOString(),
    counts,
    items,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────

type OverpassElement = {
  type: "node" | "way" | "relation";
  id: number;
  lat?: number;
  lon?: number;
  tags?: Record<string, string>;
};
type OverpassResponse = { elements?: OverpassElement[] };

function classifyMode(tags: Record<string, string>): TransportMode | null {
  const station = tags.station;
  if (station === "subway") return "metro";
  if (station === "light_rail") return "tram";

  const railway = tags.railway;
  if (railway === "tram_stop") return "tram";
  if (railway === "station" || railway === "halt") {
    // A station tagged as subway or light_rail is caught above. Some Paris
    // métro stations carry `subway=yes` instead of `station=subway`.
    if (tags.subway === "yes") return "metro";
    if (tags.light_rail === "yes" || tags.tram === "yes") return "tram";
    return "train";
  }

  if (tags.highway === "bus_stop") return "bus";

  // `public_transport=station` without a more specific tag — try heuristics.
  if (tags.public_transport === "station") {
    if (tags.subway === "yes") return "metro";
    if (tags.light_rail === "yes" || tags.tram === "yes") return "tram";
    if (tags.train === "yes") return "train";
    if (tags.bus === "yes") return "bus";
    return null;
  }

  return null;
}

/**
 * Extract the line references for a stop. OSM is inconsistent here — some
 * areas carry a `ref` tag (comma-separated numbers), some carry `route_ref`,
 * and larger networks encode lines via `route=` relations we don't traverse.
 * We take whatever we can get without a second request.
 */
function extractLines(tags: Record<string, string>): string[] {
  const raw = tags.route_ref ?? tags.ref ?? "";
  if (!raw) return [];
  return raw
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6);
}

/**
 * Two bus stops with the same name within 30 m are almost always the two
 * directions of the same stop. Keep the closer one — doesn't change the
 * "walkable" story (you reach both by the same footprint) and halves the
 * list length in dense bus corridors.
 */
function dedupeBusStops(items: TransportItem[]): TransportItem[] {
  const out: TransportItem[] = [];
  for (const it of items) {
    if (it.mode !== "bus") {
      out.push(it);
      continue;
    }
    const dup = out.find(
      (o) =>
        o.mode === "bus" &&
        o.name.toLowerCase() === it.name.toLowerCase() &&
        haversineMeters(o.lat, o.lng, it.lat, it.lng) < 30,
    );
    if (!dup) out.push(it);
  }
  return out;
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useTransports(
  ficheId: string | null,
  geocode: GeocodeResult | null,
) {
  const commune = useCommuneInfo(geocode?.insee ?? null);

  const opts = useMemo(() => {
    if (!ficheId || !geocode || !commune) return null;
    const radius = TRANSPORTS_RADIUS_BY_TIER[commune.tier];
    return {
      ficheId,
      blockId: "transports" as const,
      inputsKey: `v5-pan|${coordKey(geocode.lat, geocode.lng)}|r${radius}|t${commune.tier}`,
      ttlDays: TRANSPORTS_TTL_DAYS,
      fetcher: () =>
        fetchTransports(geocode.lat, geocode.lng, radius, commune.tier),
    };
  }, [ficheId, geocode, commune]);

  return useEnvBlock<TransportsData>(opts);
}
